import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Redirect, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Elevation } from '../../utils';
import { ROUTES } from '../../Constants';
import { acceptReservation, rejectReservation } from '../../lib/taskrouter';
import {
  Alert as BaseAlert,
  Button,
  Script,
  Card as BaseCard,
  LinkText,
  Line,
  Carousel
} from '../../components';
import { startCall as startCallAction } from '../../lib/redux';
import { BackHomeButton } from '../../components/BackHomeButton';
import { JobBanner } from '../../components/JobBanner';
import { MessageBox } from '../../components/MessageBox';

const InstructionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Card = styled(BaseCard)`
  padding: 0;
`;

const InnerWrapper = styled.div`
  flex: 1;
  max-height: 380px;
  overflow: auto;
  padding: 16px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px;
  padding-top: 40px;
  padding-bottom: 40px;
  width: 95%;
`;

const Alert = styled(BaseAlert)`
  left: 0;
  right: 0;
  top: 72px;
  z-index: ${Elevation.Alert};
`;

function Instructions({ isAvailable, startCall, task, history }) {
  const [step, setStep] = useState(0);
  const [hasError, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!isAvailable) {
    return <Redirect to={ROUTES.HOME} />;
  }

  let agentName = 'Agent';
  let workerName = 'Worker';
  let scripts = [];
  let group = '';
  let topic = '';

  if (task) {
    if (task.taskInput) {
      const taskInput = JSON.parse(task.taskInput);

      agentName = taskInput.agent;
      workerName = taskInput.worker;
      scripts = taskInput.prompts;
      ({ topic, group } = taskInput);
    }
  }

  return (
    <>
      {hasError && (
        <Alert error danger fontWeight={700}>
          Looks like something went wrong. Try refreshing the page and changing the status again
        </Alert>
      )}
      <InstructionsWrapper>
        <JobBanner />
        <BackHomeButton
          onClick={async () => {
            await rejectReservation(true);
            history.push(ROUTES.HOME);
          }}
        />

        <Card>
          <Carousel step={step} onStepChange={setStep}>
            <Carousel.Pane>
              <InnerWrapper>
                {/* eslint-disable-next-line react/no-danger */}
                <div dangerouslySetInnerHTML={{ __html: task.jobInstructions }} />
              </InnerWrapper>
            </Carousel.Pane>

            <Carousel.Pane>
              <InnerWrapper>
                <MessageBox group={group} topic={topic} />
                {scripts.map((script, index) => (
                  <Script
                    customer={script.type !== 'agent'}
                    // the array provided doesn't have any ids, disabling eslint
                    // eslint-disable-next-line react/no-array-index-key
                    key={`script-${index}`}
                    user={script.type === 'agent' ? agentName : workerName}
                  >
                    {script.label}
                  </Script>
                ))}
              </InnerWrapper>
            </Carousel.Pane>
          </Carousel>

          <Line />

          <ButtonsWrapper>
            <LinkText
              margin={{ vertical: 'auto' }}
              fontSize="16"
              onClick={async e => {
                try {
                  e.persist();

                  if (e.target.classList.contains('disabled')) {
                    return;
                  }

                  e.target.classList.add('disabled');

                  try {
                    await rejectReservation(true);
                    history.push(ROUTES.HOME);
                    return;
                  } catch (error) {
                    e.target.classList.remove('disabled');
                  }
                } catch (err) {
                  setError(true);
                }
              }}
            >
              Skip this call
            </LinkText>

            <Button
              wrapper
              margin="0"
              disabled={loading}
              onClick={async () => {
                setLoading(true);

                try {
                  await acceptReservation();
                  await startCall(task.taskId, task.jobTypeCode);
                  history.push(ROUTES.JOB_CALL);
                  return;
                } catch (err) {
                  history.push(ROUTES.JOB_CALL, { withError: true });
                }
              }}
            >
              Start Call
            </Button>
          </ButtonsWrapper>
        </Card>
      </InstructionsWrapper>
    </>
  );
}

Instructions.propTypes = {
  isAvailable: PropTypes.bool.isRequired,
  startCall: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired,
  task: PropTypes.shape({
    jobInstructions: PropTypes.string,
    taskId: PropTypes.string,
    taskInput: PropTypes.string,
    jobTypeCode: PropTypes.string
  })
};

Instructions.defaultProps = {
  task: {}
};

function mapDispatchToProps(dispatch) {
  return {
    startCall: (...args) => dispatch(startCallAction(...args))
  };
}

function mapStateToProps({ me, tasks }) {
  return { isAvailable: me.isAvailable, task: tasks.currentTask.task };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(Instructions));
